import { Component, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import {
  ConfiguracionSistemaRequest,
  EstadoSistema,
  DEFAULT_SYSTEM_CONFIG,
} from '../../../core/models/configuracion-sistema.model';
import { SystemConfigStore } from '../../../core/stores/system-config.store';
import { ConfiguracionSistemaService } from '../../../core/services/configuracion-sistema.service';
import { PageHeaderComponent } from '../../../shared/page-header/page-header';
import { UiButtonComponent } from '../../../shared/ui-button/ui-button';
import { SelectComponent } from '../../../shared/select/select';
import { LoadingComponent } from '../../../shared/loading/loading';
import { AlertBannerComponent } from '../../../shared/alert-banner/alert-banner';

@Component({
  selector: 'app-configuracion-sistema',
  standalone: true,
  imports: [
    FormsModule,
    DatePipe,
    PageHeaderComponent,
    UiButtonComponent,
    SelectComponent,
    LoadingComponent,
    AlertBannerComponent,
  ],
  templateUrl: './configuracion.html',
  styleUrl: './configuracion.css',
})
export class ConfiguracionSistemaComponent {
  protected readonly configStore = inject(SystemConfigStore);
  private readonly service = inject(ConfiguracionSistemaService);

  protected readonly loading = this.configStore.loading;
  protected readonly saving = signal(false);
  protected readonly checkingStatus = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly successMessage = signal('');
  protected readonly statusError = signal('');
  protected readonly systemStatus = signal<EstadoSistema | null>(null);
  protected configuration: ConfiguracionSistemaRequest = {
    moneda: 'USD',
    zonaHoraria: 'America/Guayaquil',
    formatoFecha: 'yyyy-MM-dd',
  };
  protected lastUpdated = '';
  protected readonly dateFormatOptions = [{ value: 'yyyy-MM-dd', label: 'YYYY-MM-DD' }];

  constructor() {
    this.load();
  }

  protected load(): void {
    this.errorMessage.set('');
    this.configStore.load().subscribe({
      next: (response) => {
        if (response.data) {
          const { fechaActualizacion, ...configuration } = response.data;
          this.configuration = configuration;
          this.lastUpdated = fechaActualizacion;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage.set(error.error?.message ?? 'No se pudo cargar la configuración.');
      },
    });
    this.checkStatus();
  }

  protected save(): void {
    this.saving.set(true);
    this.successMessage.set('');
    this.errorMessage.set('');
    this.configStore.save(this.configuration).subscribe({
      next: (response) => {
        this.lastUpdated = response.data?.fechaActualizacion ?? new Date().toISOString();
        this.successMessage.set('La configuración se actualizó correctamente.');
        this.saving.set(false);
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage.set(error.error?.message ?? 'No se pudo guardar la configuración.');
        this.saving.set(false);
      },
    });
  }

  protected checkStatus(): void {
    this.checkingStatus.set(true);
    this.statusError.set('');
    this.service.getStatus().subscribe({
      next: (response) => {
        this.systemStatus.set(response.data ?? null);
        this.checkingStatus.set(false);
      },
      error: (error: HttpErrorResponse) => {
        this.statusError.set(error.error?.message ?? 'No se pudo consultar el estado del sistema.');
        this.checkingStatus.set(false);
      },
    });
  }

  protected restoreDefaults(): void {
    this.configuration = { ...DEFAULT_SYSTEM_CONFIG };
    this.successMessage.set(
      'Valores predeterminados restaurados. Guarda los cambios para aplicarlos.',
    );
    this.errorMessage.set('');
  }
}
